/**
 * Value Matching API accessors.
 *
 * Pure API call functions — no data transformation.
 * In local dev, the webpack dev server intercepts these GET requests
 * and returns mock data from src/mocks/api/*.json (see configUtils.js).
 */

import { monitoredFetch } from 'src/utils/helpers';
import {
  CampaignType,
  OperationType,
  ListCampaignsRequest,
  ListCampaignsResponse,
  GetCampaignConfigurationRequest,
  GetCampaignConfigurationResponse,
  CreateCampaignRequest,
  CreateCampaignResponse,
  GetCampaignDetailsRequest,
  GetCampaignDetailsResponse,
  ModifyCampaignRequest,
  ModifyCampaignResponse,
  CancelCampaignRequest,
  CancelCampaignResponse,
} from 'src/@types/valueMatching';
import { buildListCampaignsUrl } from 'src/utils/valueMatching/list/campaignListUtils';
import { buildCampaignConfigUrl } from 'src/utils/valueMatching/campaignConfig';
import { isValidCampaignType } from 'src/utils/valueMatching/commonUtils';
import { API_ENDPOINTS } from 'src/routes/valueMatching/routes';

/**
 * Fetches the list of campaign overviews from the BFF layer via GET.
 *
 * campaignType is appended as a path segment on the URL (e.g. /list-campaigns/DEAL).
 * The campaignType is validated against the CampaignType enum before making the request.
 *
 * @param request - The list campaigns request containing the campaignType to filter by
 * @returns Promise resolving to the raw ListCampaignsResponse from the API
 * @throws Error if the campaignType is not a valid enum value
 */
export async function fetchListCampaigns(request: ListCampaignsRequest): Promise<ListCampaignsResponse> {
  if (!isValidCampaignType(request.campaignType)) {
    throw new Error(`Invalid campaign type: "${request.campaignType}". Expected one of: ${Object.values(CampaignType).join(', ')}`);
  }

  const url = buildListCampaignsUrl(request.campaignType);
  return monitoredFetch<ListCampaignsResponse>({
    url,
    operation: 'ListCampaigns',
    method: 'GET',
    signal: request.signal,
  });
}

/**
 * Fetches campaign configuration from the BFF layer via GET.
 *
 * campaignType is appended as a path segment on the URL
 * (e.g. /valuematching-api/v1/campaign-config/NO_COST_EMI).
 *
 * @param request - campaignType + optional AbortSignal
 * @returns Promise resolving to GetCampaignConfigurationResponse
 * @throws Error if the campaignType is not a valid enum value
 */
export async function fetchCampaignConfiguration(
  request: GetCampaignConfigurationRequest,
): Promise<GetCampaignConfigurationResponse> {
  if (!isValidCampaignType(request.campaignType)) {
    throw new Error(
      `Invalid campaign type: "${request.campaignType}". Expected one of: ${Object.values(CampaignType).join(', ')}`,
    );
  }

  if (!Object.values(OperationType).includes(request.operationType)) {
    throw new Error(
      `Invalid operation type: "${request.operationType}". Expected one of: ${Object.values(OperationType).join(', ')}`,
    );
  }

  const url = buildCampaignConfigUrl(request.campaignType, request.operationType);
  return monitoredFetch<GetCampaignConfigurationResponse>({
    url,
    operation: 'GetCampaignConfiguration',
    method: 'GET',
    signal: request.signal,
  });
}

/**
 * Creates a new campaign by sending campaign data to the BFF layer via POST.
 *
 * @param request - The create campaign request containing the campaignData payload
 * @returns Promise resolving to CreateCampaignResponse with operationStatus, optional campaignId, and optional conflictingCampaignASINs
 * @throws Error propagated from monitoredFetch if the HTTP request fails
 */
export async function createCampaign(request: CreateCampaignRequest): Promise<CreateCampaignResponse> {
  return monitoredFetch<CreateCampaignResponse>({
    url: API_ENDPOINTS.VALUE_MATCHING_CREATE_CAMPAIGN,
    operation: 'CreateCampaign',
    method: 'POST',
    input: { campaignData: request.campaignData },
  });
}

/**
 * Modifies an existing campaign by sending updated campaign data to the BFF layer via POST.
 *
 * The BFF expects `campaignId` at the top level (separate from `campaignData`).
 * `monitoredFetch` handles JSON serialization and CSRF token injection automatically.
 *
 * @param request - The modify campaign request containing campaignId and campaignData
 * @returns Promise resolving to ModifyCampaignResponse with operationStatus and optional conflictingCampaignASINs
 * @throws Error propagated from monitoredFetch if the HTTP request fails
 */
export async function modifyCampaign(request: ModifyCampaignRequest): Promise<ModifyCampaignResponse> {
  return monitoredFetch<ModifyCampaignResponse>({
    url: API_ENDPOINTS.VALUE_MATCHING_MODIFY_CAMPAIGN,
    operation: 'ModifyCampaign',
    method: 'POST',
    input: { campaignId: request.campaignId, campaignData: request.campaignData },
  });
}

/**
 * Fetches full campaign details from the BFF layer via GET.
 *
 * campaignId and version are path segments; campaignType is a required query parameter
 * for PAAM authorization (e.g. /valuematching-api/v1/campaign/{campaignId}/{version}?campaignType=NO_COST_EMI).
 *
 * @param request - campaignId, version, campaignType, and optional AbortSignal
 * @returns Promise resolving to GetCampaignDetailsResponse containing the full CampaignData
 * @throws Error if campaignType is not a valid enum value
 * @throws Error propagated from monitoredFetch if the HTTP request fails
 */
export async function fetchCampaignDetails(
  request: GetCampaignDetailsRequest,
): Promise<GetCampaignDetailsResponse> {
  if (!isValidCampaignType(request.campaignType)) {
    throw new Error(
      `Invalid campaign type: "${request.campaignType}". Expected one of: ${Object.values(CampaignType).join(', ')}`,
    );
  }

  const url = `${API_ENDPOINTS.VALUE_MATCHING_CAMPAIGN_DETAILS}/${encodeURIComponent(request.campaignId)}/${encodeURIComponent(String(request.version))}?campaignType=${encodeURIComponent(request.campaignType)}`;
  return monitoredFetch<GetCampaignDetailsResponse>({
    url,
    operation: 'GetCampaignDetails',
    method: 'GET',
    signal: request.signal,
  });
}

/**
 * Cancels an existing campaign by sending its identity + type to the BFF via POST.
 * The BFF injects `marketplaceId` and `requestedBy` server-side, so the UI sends
 * only `campaignId`, `version`, and `campaignType`. `monitoredFetch` JSON-serialises the `input` and adds the CSRF token automatically.
 *
 * @param request - The cancel campaign request containing campaignId, version, and campaignType
 * @returns Promise resolving to CancelCampaignResponse with operationStatus
 * @throws Error if campaignType is not a valid enum value
 * @throws Error propagated from monitoredFetch if the HTTP request fails
 */
export async function cancelCampaign(request: CancelCampaignRequest): Promise<CancelCampaignResponse> {
  if (!isValidCampaignType(request.campaignType)) {
    throw new Error(
      `Invalid campaign type: "${request.campaignType}". Expected one of: ${Object.values(CampaignType).join(', ')}`,
    );
  }

  return monitoredFetch<CancelCampaignResponse>({
    url: API_ENDPOINTS.VALUE_MATCHING_CANCEL_CAMPAIGN,
    operation: 'CancelCampaign',
    method: 'POST',
    input: {
      campaignId: request.campaignId,
      version: request.version,
      campaignType: request.campaignType,
    },
  });
}
